<template>
  <div
    class="image-lightbox"
    role="dialog"
    aria-modal="true"
    :aria-label="dialogLabel"
    data-qa="accessory-image-lightbox"
    @click.self="$emit('close')"
  >
    <button
      ref="close"
      class="image-lightbox__close"
      type="button"
      :title="i18n.t('/AccessoryCatalog/CloseTooltip')"
      :aria-label="i18n.t('/AccessoryCatalog/CloseTooltip')"
      data-qa="accessory-image-lightbox-close"
      @click="$emit('close')"
    >
      <icon icon="close" aria-hidden="true" />
    </button>

    <div class="image-lightbox__stage" @click.self="$emit('close')">
      <button
        v-if="hasMultiple"
        class="image-lightbox__nav image-lightbox__nav--previous"
        type="button"
        :title="i18n.t('/AccessoryCatalog/PreviousImage')"
        :aria-label="i18n.t('/AccessoryCatalog/PreviousImage')"
        data-qa="accessory-image-lightbox-previous"
        @click="step(-1)"
      >
        <icon icon="angle-left" aria-hidden="true" />
      </button>

      <!-- Not an accessory-thumb: this is the one photo the user deliberately
           opened, so it loads eagerly, and it fills a full-screen black overlay
           rather than a tile -->
      <img
        v-if="source && !failed"
        class="image-lightbox__img"
        :src="source"
        :alt="alt"
        data-qa="accessory-image-lightbox-image"
        @error="failed = true"
      />
      <p v-else class="image-lightbox__empty">{{ i18n.t('/AccessoryCatalog/NoPhoto') }}</p>

      <button
        v-if="hasMultiple"
        class="image-lightbox__nav image-lightbox__nav--next"
        type="button"
        :title="i18n.t('/AccessoryCatalog/NextImage')"
        :aria-label="i18n.t('/AccessoryCatalog/NextImage')"
        data-qa="accessory-image-lightbox-next"
        @click="step(1)"
      >
        <icon icon="angle-right" aria-hidden="true" />
      </button>
    </div>

    <div v-if="hasMultiple" class="image-lightbox__counter" role="status" aria-live="polite">
      {{ counter }}
    </div>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import modalFocus from '@/mixins/modalFocus'

export default {
  components: {
    Icon,
  },
  mixins: [modalFocus],
  props: {
    images: {
      type: Array,
      required: true,
    },
    /** Index of the photo on screen; kept in sync with the panel behind it */
    index: {
      type: Number,
      default: 0,
    },
    alt: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'update:index'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      failed: false,
    }
  },
  computed: {
    // The panel may hand over an index from a gallery it has since replaced
    current() {
      return Math.min(Math.max(this.index, 0), this.images.length - 1)
    },
    source() {
      return this.images[this.current] || null
    },
    hasMultiple() {
      return this.images.length > 1
    },
    counter() {
      return this.i18n.t('/AccessoryCatalog/ImageCounter', {
        index: this.current + 1,
        count: this.images.length,
      })
    },
    dialogLabel() {
      return this.alt || this.i18n.t('/AccessoryCatalog/EnlargeTooltip')
    },
  },
  watch: {
    // A photo that failed says nothing about the next one
    source() {
      this.failed = false
    },
  },
  mounted() {
    // Capture, so Escape closes the photo without also closing the detail panel
    document.addEventListener('keydown', this.onKeydown, true)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown, true)
  },
  methods: {
    /** The close button, rather than whatever the focus trap finds first */
    modalFocusFirst() {
      return this.$refs.close
    },
    step(delta) {
      const count = this.images.length
      if (!count) return
      this.$emit('update:index', (this.current + delta + count) % count)
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation()
        event.preventDefault()
        this.$emit('close')
        return
      }
      if (!this.hasMultiple) return
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.step(-1)
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.step(1)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// No global font-size scale exists in styles/variables; the overlay draws two
// pieces of text and they share one size.
$overlay-text-size: 12px;

.image-lightbox {
  position: fixed;
  inset: 0;
  z-index: z-index(prompt);
  display: grid;
  grid-template-rows: 1fr auto;
  gap: 12px;
  padding: 3vh 3vw;
  background: rgba(0, 0, 0, 0.82);
  animation: lightbox-fade 0.14s ease;

  &__close {
    position: absolute;
    // Above the photo, which is positioned too and comes later in the markup
    z-index: 1;
    top: 14px;
    right: 16px;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    color: $white;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background 0.14s ease;

    // The app sets a global `*:focus { outline: 1px dotted }` in styles/app/_accessibility.scss,
    // which paints on mouse click too. Suppress it here so only keyboard focus is indicated.
    &:focus {
      outline: none;
    }

    &:hover,
    &:focus-visible {
      background: rgba(255, 255, 255, 0.26);
      outline: none;
    }

    svg {
      @include icon;
      width: 18px;
      height: 18px;
    }
  }

  // A single explicit row and column: the photo is stretched over the whole
  // stage, and the nav buttons are positioned on top of it
  &__stage {
    position: relative;
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
  }

  &__img {
    width: 100%;
    height: 100%;
    min-height: 0;
    object-fit: contain;
    border-radius: 8px;
  }

  &__empty {
    margin: 0;
    place-self: center;
    font-size: $overlay-text-size;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.75);
  }

  &__nav {
    position: absolute;
    z-index: 1;
    top: 50%;
    transform: translateY(-50%);
    width: 46px;
    height: 46px;
    border: 0;
    border-radius: 50%;
    // Dark, so it reads over the white letterbox of the photo as well
    background: rgba(0, 0, 0, 0.55);
    color: $white;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background 0.14s ease;

    // The app sets a global `*:focus { outline: 1px dotted }` in styles/app/_accessibility.scss,
    // which paints on mouse click too. Suppress it here so only keyboard focus is indicated.
    &:focus {
      outline: none;
    }

    &:hover,
    &:focus-visible {
      background: rgba(0, 0, 0, 0.78);
      outline: none;
    }

    &--previous {
      left: 12px;
    }

    &--next {
      right: 12px;
    }

    svg {
      @include icon;
      width: 22px;
      height: 22px;
    }
  }

  &__counter {
    text-align: center;
    font-size: $overlay-text-size;
    letter-spacing: 0.04em;
    color: rgba(255, 255, 255, 0.75);
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: $screen-md) {
    padding: 2vh 2vw;

    &__nav {
      width: 38px;
      height: 38px;

      &--previous {
        left: 6px;
      }

      &--next {
        right: 6px;
      }
    }
  }
}

@keyframes lightbox-fade {
  from {
    opacity: 0;
  }
}
</style>
